import { ButtonUtils, ObjectBuilder, Rectangle3D, ScrollViewer3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { Container } from "@babylonjs/gui/2D/controls/container";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { text } from "../../TextResources";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { ScrollHandler } from "../ScrollHandler";
import { MeshName } from "../MeshName";
import { XRInputHelper } from "../XRInputHelper";
import { LiveRelatedItemLayoutHelper } from "./LiveGalleryItemLayoutHelper";
import { BroadcastManagerState } from "@dreamcam/web-sdk";
import { AppView } from "../AppView";
import { MessageType } from "../../interfaces/NotificationCenter";
export class BroadcastMenuRelatedPanel {
    constructor(mesh2d, relatedBroadcastsPageSize, panelCallbacks, viewCallbacks, infoDialog, loginDialog, messageDialog) {
        this._enteringRelatedBroadcast = false;
        this._bmState = BroadcastManagerState.UNINITIALIZED;
        this._hoverChangedCallback = (itemIndex, state) => {
            const relatedItem = this._relatedItems[itemIndex];
            if (relatedItem.broadcastThumbnail === undefined)
                return;
            // Unhover all:
            this._relatedItems.forEach(ri => {
                if (ri.broadcastThumbnail === undefined)
                    return;
                ri.layoutHelper.changeState("normal", ri.broadcastThumbnail, ri.controls);
            });
            if (state === "hover") {
                // Hover one:
                relatedItem.layoutHelper.changeState("hover", relatedItem.broadcastThumbnail, relatedItem.controls);
            }
        };
        this._mesh2d = mesh2d;
        this._panelCallbacks = panelCallbacks;
        this._callbacks = viewCallbacks;
        this._infoDialog = infoDialog;
        this._loginDialog = loginDialog;
        this._messageDialog = messageDialog;
        this._scrollHandler = this._createScrollHandler();
        this.isVisible = false;
        const rp = Env.broadcast.menu.relatedPanel;
        this._root = new Rectangle3D("RelatedPanelRoot");
        this._root.background = rp.root.background;
        this._root.cornerRadius = this._mesh2d.m2px(rp.root.cornerRadius);
        this._root.thickness = 0;
        this._mesh2d.texture.addControl(this._root);
        const header = new Container();
        header.background = rp.header.background;
        header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        header.heightInPixels = this._mesh2d.m2px(rp.header.height);
        this._root.addControl(header);
        header.addControl(ObjectBuilder.createTextBlock3D(this._mesh2d, "RelatedHeaderText", text.broadcast_menu_relatedPanel_header, rp.header.caption));
        const closeButton = ButtonUtils.CreateImgButton("RelatedCloseButton", rp.closeButton, this._mesh2d);
        closeButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        closeButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        closeButton.topInPixels = this._mesh2d.m2px(rp.closeButton.top);
        closeButton.leftInPixels = this._mesh2d.m2px(-rp.closeButton.right);
        closeButton.onClickObserver.add(() => {
            this._closePanel();
        });
        this._root.addControl(closeButton);
        this._relatedItems = new Array(relatedBroadcastsPageSize);
        this._createStub();
        this._createContent(relatedBroadcastsPageSize);
        this._setStubVisibility(true);
    }
    _closePanel() {
        setTimeout(() => {
            this.hide();
            this._panelCallbacks.onClose();
        }, 0);
    }
    _setStubVisibility(visible) {
        this._stubContainer.isVisible = visible;
        this._relatedBroadcastsScrollViewer.isVisible = !visible;
    }
    _createScrollHandler() {
        const speed = Env.broadcast.menu.relatedPanel.scrollView.speed;
        const scrollHandler = new ScrollHandler([MeshName.broadcastMenuPanelRelated], this._mesh2d.m2px(speed), (v) => {
            this._relatedBroadcastsScrollViewer.verticalValue = Math.max(Math.min(this._relatedBroadcastsScrollViewer.verticalValue +
                v / (this._relatedBroadcastsScrollViewer.children[0].heightInPixels - this._relatedBroadcastsScrollViewer.heightInPixels), 1), 0);
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2d);
        pointerAddon.onPointerOut.add(() => {
            scrollHandler.stopScroll();
        });
        return scrollHandler;
    }
    get isVisible() {
        return this._mesh2d.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        if (value) {
            this._scrollHandler.addThumbstickAxesChangedListener();
            this._panelCallbacks.onShow();
        }
        else {
            this._scrollHandler.releaseThumbstickAxesChangedListener();
        }
        this._mesh2d.isVisible = value;
    }
    show() {
        this.isVisible = true;
    }
    hide() {
        this.isVisible = false;
    }
    stateChanged(state, isGuest) {
        this._bmState = state;
        if (!isGuest) {
            this._messageDialog.hide();
            this._loginDialog?.hide();
        }
    }
    outputRelatedBroadcastUpdated(broadcastThumbnail) {
        // console.log(`%c++++ index: ${index}, name: ${relatedBroadcast?.modelNickname ?? "null"}`, "background-color: lime");
        const relatedItem = this._relatedItems[broadcastThumbnail.index];
        if (relatedItem.broadcastThumbnail === undefined) {
            if (!relatedItem.controls.favoriteButton.onPointerClickObservable.hasObservers()) {
                relatedItem.controls.favoriteButton.onPointerClickObservable.add(async (ev) => {
                    if (this._enteringRelatedBroadcast)
                        return;
                    await this._favoriteButtonHandlerAsync(ev, relatedItem);
                });
            }
            if (!relatedItem.controls.thumbnailContainer.onPointerClickObservable.hasObservers()) {
                relatedItem.controls.thumbnailContainer.onPointerClickObservable.add(async () => {
                    if (!this._mesh2d.mesh.isPickable || this._enteringRelatedBroadcast)
                        return;
                    if (relatedItem.broadcastThumbnail !== undefined && relatedItem.broadcastThumbnail.broadcast !== null) {
                        if (this._bmState === BroadcastManagerState.MY_PRIVATE_SHOW || this._bmState === BroadcastManagerState.SPY_SHOW) {
                            AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.info, this._bmState === BroadcastManagerState.MY_PRIVATE_SHOW
                                ? text.broadcast_menu_privateShow_leaveWarning
                                : text.broadcast_menu_spyMode_leaveWarning);
                        }
                        else {
                            this._enteringRelatedBroadcast = true;
                            try {
                                this._messageDialog.hide();
                                this._loginDialog?.hide();
                                this._closePanel();
                                await this._callbacks.openBroadcastByIdAsync(relatedItem.broadcastThumbnail.broadcast.id);
                            }
                            finally {
                                this._enteringRelatedBroadcast = false;
                            }
                        }
                    }
                });
            }
        }
        relatedItem.broadcastThumbnail = broadcastThumbnail;
        relatedItem.layoutHelper.updateControls(broadcastThumbnail, relatedItem.controls);
        relatedItem.layoutHelper.updateImage(broadcastThumbnail.thumbnailImages, relatedItem.controls);
        this._setStubVisibility(broadcastThumbnail.index === 0 && broadcastThumbnail.broadcast === null);
    }
    outputRelatedBroadcastImageChanged(index, links) {
        // console.log(`%c++++ index: ${index}, name: ${links.length > 0 ? links[0] : "null"}`, "background-color: yellow");
        // if (links.length > 0) links[0] = "https://sdafdsa.com/sdfsd.jpg"
        const relatedItem = this._relatedItems[index];
        if (relatedItem.broadcastThumbnail === undefined)
            return;
        relatedItem.layoutHelper.updateImage(links, relatedItem.controls);
    }
    _createStub() {
        const rp = Env.broadcast.menu.relatedPanel;
        this._stubContainer = new Container("relatedBroadcastsStubContainer");
        this._stubContainer.widthInPixels = this._mesh2d.m2px(rp.width);
        this._stubContainer.heightInPixels = this._mesh2d.m2px(rp.height - rp.header.height);
        this._stubContainer.topInPixels = this._mesh2d.m2px(rp.header.height);
        this._stubContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._stubContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._root.addControl(this._stubContainer);
        const captionTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "RelatedStubCaptionText", text.broadcast_menu_relatedPanel_caption, rp.stub.caption);
        this._stubContainer.addControl(captionTextBlock);
        const descriptionTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "RelatedStubDescriptionText", text.broadcast_menu_relatedPanel_description, rp.stub.description);
        this._stubContainer.addControl(descriptionTextBlock);
    }
    _createContent(relatedBroadcastsPageSize) {
        const rp = Env.broadcast.menu.relatedPanel;
        const sv = rp.scrollView;
        this._relatedBroadcastsScrollViewer = new ScrollViewer3D("relatedBroadcastsScrollViewer");
        this._relatedBroadcastsScrollViewer.widthInPixels = this._mesh2d.m2px(rp.width);
        this._relatedBroadcastsScrollViewer.heightInPixels = this._mesh2d.m2px(rp.height - rp.header.height);
        this._relatedBroadcastsScrollViewer.topInPixels = this._mesh2d.m2px(rp.header.height);
        this._relatedBroadcastsScrollViewer.color = sv.colors.background;
        this._relatedBroadcastsScrollViewer.barColor = sv.colors.bar;
        this._relatedBroadcastsScrollViewer.hoverBarColor = sv.colors.hoverBar;
        this._relatedBroadcastsScrollViewer.pressedBarColor = sv.colors.pressedBar;
        this._relatedBroadcastsScrollViewer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._relatedBroadcastsScrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._relatedBroadcastsScrollViewer.verticalBarPaddingInPixels = {
            left: this._mesh2d.m2px(sv.verticalBar.padding.left),
            right: this._mesh2d.m2px(sv.verticalBar.padding.right),
            top: this._mesh2d.m2px(sv.verticalBar.padding.top),
            bottom: this._mesh2d.m2px(sv.verticalBar.padding.bottom)
        };
        this._relatedBroadcastsScrollViewer.barSizeInPixels = this._mesh2d.m2px(sv.verticalBar.size);
        this._root.addControl(this._relatedBroadcastsScrollViewer);
        this._relatedBroadcastsScrollViewer.autoSizeThumbs = true;
        // this._relatedBroadcastsScrollViewer.verticalValue = 0;
        this._relatedBroadcastsStackPanel = new StackPanel("relatedBroadcastsStackPanel");
        this._relatedBroadcastsStackPanel.isVertical = true;
        this._relatedBroadcastsStackPanel.spacing = this._mesh2d.m2px(rp.stackPanel.spacing);
        this._relatedBroadcastsScrollViewer.addControl(this._relatedBroadcastsStackPanel);
        const topSpacing = this._createEmptyControl("topSpacing");
        this._relatedBroadcastsStackPanel.addControl(topSpacing);
        for (let i = 0; i < relatedBroadcastsPageSize; i++) {
            const layoutHelper = new LiveRelatedItemLayoutHelper(this._mesh2d);
            const controls = layoutHelper.createControls(i, this._hoverChangedCallback);
            const relatedItem = {
                broadcastThumbnail: undefined,
                controls,
                layoutHelper,
            };
            this._relatedBroadcastsStackPanel.addControl(relatedItem.controls.thumbnailContainer);
            this._relatedItems[i] = relatedItem;
        }
        const bottomSpacing = this._createEmptyControl("bottomSpacing");
        this._relatedBroadcastsStackPanel.addControl(bottomSpacing);
        setTimeout(() => {
            this._relatedBroadcastsScrollViewer.verticalValue = 0;
        });
    }
    async _favoriteButtonHandlerAsync(eventData, relatedItem) {
        // noinspection DuplicatedCode
        const callbacks = {
            isAuthorized: () => {
                return this._callbacks.isAuthorized();
            },
            addModelToFavoritesAsync: async (modelId) => {
                await this._callbacks.addModelToFavoritesAsync(modelId);
            },
            removeModelFromFavoritesAsync: async (modelId) => {
                await this._callbacks.removeModelFromFavoritesAsync(modelId);
            },
            getInfoDialog: () => {
                return this._infoDialog;
            },
            startLoginAsync: async (_tab) => {
            },
            notifyViewsOnIsFavoriteChanged: (modelId, isFavorite) => {
                this._callbacks.notifyViewsOnIsFavoriteChanged(modelId, isFavorite);
            },
        };
        await relatedItem.layoutHelper.favoriteButtonHandlerAsync(eventData, this._messageDialog, this._loginDialog, relatedItem.broadcastThumbnail?.broadcast ?? null, callbacks, relatedItem.controls);
    }
    _createEmptyControl(name) {
        const result = new Container(name);
        result.widthInPixels = 1;
        result.heightInPixels = 1;
        return result;
    }
}
